import { useEffect, useState } from 'react'

import { Field, SelectDropdown, Slider } from '@makyoapp/makyo-ui'

import { useBreakpoint } from '@/hooks/useBreakpoint'
import { BuilderComponentSetting } from '@/shared/builder'
import { viewportMaxWidthMap } from '@/shared/main-navigation-bar'

interface SliderFieldSettingProps {
  id: string
  settingId: string
  value: any
  sliderSettings: BuilderComponentSetting['sliderSettings']
  onChange(value: any): void
}

const SliderFieldSetting = ({ id, settingId, value, sliderSettings, onChange }: SliderFieldSettingProps) => {
  const { step = 1, max = 10, min = 1, unit = '', showMark = false, showUnit = false } = sliderSettings ?? {}
  const isArr = Array.isArray(value)

  const [unitValue, setUnitValue] = useState(unit)

  const { device } = useBreakpoint()
  const maxPixel = viewportMaxWidthMap[device]

  const extractedUnit = (v: string) => v.replace(/[0-9.-]/g, '')

  useEffect(() => {
    if (isArr && typeof value[0] === 'string') {
      const v = value[0]
      setUnitValue(extractedUnit(v))
    } else if (typeof value === 'string') {
      setUnitValue(extractedUnit(value))
    } else {
      setUnitValue(unit)
    }
  }, [value, unit, isArr])

  const getMinValue = (min: number) => {
    return min
  }

  const getMaxValue = (showUnit: boolean, unitValue: string, max: number) => {
    if (!showUnit) return max
    if (unitValue === '%') return 100
    if (unitValue === 'px' && max <= 300) return max
    return maxPixel
  }

  const getStepSize = (showUnit: boolean, unitValue: string, defaultStep: number) => {
    if (!showUnit) return defaultStep // Use the setting's default step for non-unit sliders
    if (unitValue === '%') return defaultStep
    if (unitValue === 'px') {
      // For pixel values, if the max is small (like divider thickness), use smaller steps
      return max <= 10 ? 0.25 : 5
    }
    return defaultStep
  }

  let minValue = getMinValue(min)
  let maxValue = getMaxValue(showUnit, unitValue, max)

  const stepValue = getStepSize(showUnit, unitValue, step)

  const getNumericValue = (v: string | number) => {
    try {
      if (showUnit && typeof v === 'string' && v !== null) {
        const numericPart = parseFloat(v)
        const minValue = min
        return isNaN(numericPart) ? minValue : Math.max(minValue, numericPart)
      }
      const nv = Number(v)
      const minValue = min
      return isNaN(nv) ? minValue : Math.max(minValue, nv)
    } catch (error) {
      console.warn(`Error parsing slider value: ${v}`, error)
      return unitValue === 'px' ? 100 : min
    }
  }

  const numValue = isArr ? value.map(getNumericValue) : getNumericValue(value)

  let finalValue = numValue
  if (!isArr && isNaN(numValue as number)) {
    finalValue = min ?? 0
  }

  let values = finalValue as number[]
  if (!isArr) {
    const singleValue = isNaN(finalValue as number) ? 0 : (finalValue as number)
    values = [singleValue]
  }

  const finalValues =
    unitValue === '%' && values[0] > 100 ? [100] : unitValue === 'px' && values[0] > maxPixel ? [maxPixel] : values

  // todo: temporary solution for slider bug
  // need further debugging to find the root cause
  if (finalValues.length === 1 && finalValues[0] > maxValue) {
    maxValue = finalValues[0]
  }
  if (finalValues.length === 1 && finalValues[0] < minValue) {
    minValue = finalValues[0]
  }

  return (
    <div className='w-full relative' style={{ marginTop: showUnit ? '-45px' : 0 }}>
      {showUnit && (
        <div className='flex items-center justify-end'>
          <Field className='pb-0 min-h-10!'>
            <SelectDropdown
              className='min-h-8 pb-0'
              id={settingId}
              onChange={(target) => {
                setUnitValue(target)
                if (sliderSettings) sliderSettings.unit = target

                // Always update the parent value with the new unit to prevent useEffect from resetting it
                const currentNumericValue = values[0] || min
                let newValue: (string | number)[]

                if (target === '%') {
                  // Cap percentage values at 100
                  const cappedValue = Math.min(currentNumericValue, 100)
                  newValue = [`${cappedValue}%`]
                } else {
                  // For px we set to maximum
                  if (target === 'px') {
                    const getMax = Math.max(100, currentNumericValue)
                    newValue = [`${getMax}${target}`]
                  } else {
                    newValue = [`${currentNumericValue}${target}`]
                  }
                }

                onChange(newValue)
              }}
              value={unitValue || unit}
              options={['%', 'px']}
            />
          </Field>
        </div>
      )}
      <Slider
        id={settingId}
        values={finalValues}
        step={stepValue}
        min={minValue}
        max={maxValue}
        renderLabel={(v) => (unit ? `${v}${unitValue}` : v)}
        renderTrackLabel={(v) => (unit ? `${v}${unitValue}` : v)}
        showMark={showMark}
        onChange={(value) => {
          let newValue: (string | number)[] = value

          if (showUnit) {
            const selectedUnit = unitValue || unit
            newValue = [`${value}${selectedUnit}`]
          }

          onChange(newValue)
        }}
      />
    </div>
  )
}

export default SliderFieldSetting
